import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Comic } from "@/types/comic";
import { formatCount } from "@/utils/format";

interface HeroCarouselProps {
  comics: Comic[];
}

export default function HeroCarousel({ comics }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (comics.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % comics.length);
    }, 5200);
    return () => window.clearInterval(timer);
  }, [comics.length]);

  if (comics.length === 0) return null;
  const active = comics[index];
  const firstChapter = active.chapters[0];

  const go = (dir: number) =>
    setIndex((prev) => (prev + dir + comics.length) % comics.length);

  return (
    <section className="w-full px-4 md:px-6 pt-6 md:pt-8">
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary-50 via-background-100 to-secondary-50 border border-background-200">
        <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-primary-200/40 blur-2xl"></div>
        <div className="absolute -bottom-20 -left-16 w-72 h-72 rounded-full bg-accent-100/50 blur-2xl"></div>

        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 p-5 md:p-10 lg:p-12 items-center">
          <div key={active.id} className="animate-fade-up order-2 lg:order-1">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span
                className={`px-2.5 py-1 rounded-md text-xs font-medium ${
                  active.status === "连载"
                    ? "bg-primary-500 text-background-50"
                    : "bg-secondary-700 text-background-50"
                }`}
              >
                {active.status}
              </span>
              <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-background-50 text-foreground-700 border border-background-200">
                {active.genres.join(" · ")}
              </span>
            </div>
            <h1 className="font-heading font-extrabold text-2xl md:text-4xl lg:text-[40px] leading-tight text-foreground-950">
              {active.title}
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              <i className="ri-user-3-line mr-1"></i>
              {active.author} · 评分 {active.rating.toFixed(1)} · {formatCount(active.favorites)} 人收藏
            </p>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700 line-clamp-3 max-w-xl">
              {active.description}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={firstChapter ? `/read/${active.id}/${firstChapter.id}` : `/detail/${active.id}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-fill text-lg"></i>
                开始阅读
              </Link>
              <Link
                to={`/detail/${active.id}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50 text-foreground-800 font-medium text-sm border border-background-300 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-information-line text-lg"></i>
                查看详情
              </Link>
            </div>
          </div>

          <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
            <div className="relative w-48 sm:w-56 lg:w-72 shrink-0">
              <div className="absolute inset-0 translate-x-3 translate-y-3 rounded-xl bg-primary-300/50"></div>
              <Link to={`/detail/${active.id}`} className="relative block cursor-pointer">
                <div key={active.id} className="w-full aspect-[3/4] rounded-xl overflow-hidden border border-background-200 bg-background-200 animate-fade-up">
                  <img
                    src={active.cover}
                    alt={`${active.title} 封面`}
                    title={`${active.title} 漫画封面`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* controls */}
        <button
          type="button"
          aria-label="上一部"
          onClick={() => go(-1)}
          className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/90 text-foreground-800 flex items-center justify-center hover:bg-background-50 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一部"
          onClick={() => go(1)}
          className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/90 text-foreground-800 flex items-center justify-center hover:bg-background-50 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
          {comics.map((c, i) => (
            <button
              key={c.id}
              type="button"
              aria-label={`切换到 ${c.title}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-6 bg-primary-500" : "w-1.5 bg-foreground-300"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}